import {
  MetricType,
  MetricConfig,
  TransformedMetricData,
} from "components/performance/PerformanceTypes";
import { PerformanceReportState } from "store/performance-report/PerformanceReportReducers";
import {
  MetricCategory,
  PieChartState,
  LocationTreeNode,
} from "store/performance-report/PerformanceReportTypes";
import { IntlShape } from "react-intl";
import { KPI_PRODUCT_METRICS_OPTIONS } from "./Constants";

export const getChartPropertyName = (
  dimensionId: string,
): keyof PieChartState => {
  switch (dimensionId) {
    case "channelGroup":
      return "trafficBySource";
    case "deviceCategory":
      return "trafficByDevice";
    case "ordersSourceGroup":
      return "ordersBySource";
    case "ordersDeviceCategory":
      return "ordersByDevice";
    default:
      throw new Error(`Unknown dimension ID: ${dimensionId}`);
  }
};

export const TRAFFIC_METRICS_PAYLOAD = [
  { id: "visits", name: "Visits" },
  { id: "uniqueVisitors", name: "Unique Visitors" },
  { id: "bounceRate", name: "Bounce Rate" },
  { id: "pageViews", name: "Page Views" },
  { id: "pageViewsPerVisit", name: "Page Views Per Visit" },
  { id: "averageVisitDuration", name: "Average Visit Duration" },
];

export const CONVERSION_METRICS_PAYLOAD = [
  { id: "cartToCheckoutRate", name: "Cart to Checkout Rate" },
  { id: "checkoutCompletionRate", name: "Checkout Completion Rate" },
  { id: "cartAbandonmentRate", name: "Cart Abandonment Rate" },
  { id: "conversionRate", name: "Conversion Rate" },
];

export const PRODUCT_METRICS_PAYLOAD = [
  { id: "productViewEvents", name: "Product Views" },
  { id: "productViewsPerVisit", name: "Product Views / Visit" },
  { id: "addToCartEvents", name: "Total Adds to Cart (ATC)" },
  { id: "cartToViewRate", name: "Adds to Cart / View" },
];

export const REVENUE_METRICS_PAYLOAD = [
  { id: "totalRevenue", name: "Total Revenue" },
  { id: "revenuePerVisit", name: "Revenue Per Visit (RPV)" },
  { id: "averageOrderValue", name: "Average Order Value (AOV)" },
];

export const calculateDimensionId = (dateRangeDays: number): string => {
  if (dateRangeDays > 30) return "yearMonth";
  if (dateRangeDays > 14) return "yearWeek";
  return "date";
};

export const calculateGranularity = (
  dateRangeDays: number,
): "day" | "week" | "month" => {
  if (dateRangeDays > 30) return "month";
  if (dateRangeDays > 14) return "week";
  return "day";
};

export const createMetricLineCharts = (
  dateYearMetricsData: any,
  metricsConfig: MetricConfig[],
  granularity: "day" | "week" | "month",
  intl: IntlShape,
  categoryPrefix: string,
): Record<string, any> => {
  return metricsConfig.reduce(
    (acc, config) => {
      acc[config.id] = transformDateYearMetricsData(
        dateYearMetricsData,
        intl.formatMessage(config.title),
        intl.formatMessage({
          id: `performance.${categoryPrefix}.currentPeriod`,
          defaultMessage: "Current Period",
        }),
        intl.formatMessage({
          id: `performance.${categoryPrefix}.previousPeriod`,
          defaultMessage: "Previous Period",
        }),
        granularity,
        config.id,
      );
      return acc;
    },
    {} as Record<string, any>,
  );
};

export const PIE_CHART_PAYLOADS = {
  TRAFFIC_BY_SOURCE: {
    dimension: { id: "channelGroup" },
    metrics: [{ id: "visits" }],
    sortBy: { metricId: "visits", desc: true },
    limit: 4,
  },
  TRAFFIC_BY_DEVICE: {
    dimension: { id: "deviceCategory" },
    metrics: [{ id: "visits" }],
    sortBy: { metricId: "visits", desc: true },
    limit: 4,
  },
  ORDERS_BY_SOURCE: {
    dimension: { id: "channelGroup" },
    metrics: [{ id: "orders" }],
    sortBy: { metricId: "orders", desc: true },
    limit: 4,
  },
  ORDERS_BY_DEVICE: {
    dimension: { id: "deviceCategory" },
    metrics: [{ id: "orders" }],
    sortBy: { metricId: "orders", desc: true },
    limit: 4,
  },
};

export const PIE_CHART_COLORS = [
  "primary.main",
  "neutral.green",
  "neutral.yellow",
  "neutral.linkBlue",
  "neutral.red",
  "neutral.darkGreen",
  "neutral.shadowGrey",
  "neutral.darkBlue",
  "neutral.seaSerpent",
  "neutral.shakeSpeareColor",
];

const POSITIVE_METRICS = [
  "visits",
  "uniqueVisitors",
  "pageViews",
  "pageViewsPerVisit",
  "averageVisitDuration",
  "product_views",
  "add_to_cart",
  "revenue",
  "conversion_rate",
];

export const transformMetricsForPayload = (
  metrics: { id: string; name: string }[],
) => {
  return metrics.map((m) => ({ id: m.id }));
};

export const transformMetricsData = (
  metricsConfig: MetricConfig[],
  metricsData: { metrics: Record<string, any>; isLoading: boolean },
  intl: IntlShape,
): TransformedMetricData[] => {
  return metricsConfig.map((config) => {
    const metricData = metricsData.metrics[config.id];
    return {
      id: config.id,
      title: intl.formatMessage(config.title),
      displayValue: metricData
        ? formatMetricValue(metricData.current, config)
        : "-",
      percentageChange: metricData?.percentageChange,
      polarity: config.polarity,
      isLoading: metricsData.isLoading,
    };
  });
};

export const transformPieChartsData = (
  chartsConfig: any[],
  dataSourceMap: Record<string, { data: any; isLoading: boolean }>,
  intl: IntlShape,
  theme: any,
) => {
  return chartsConfig.map((config) => {
    const chartData = dataSourceMap[config.id];

    if (!chartData || chartData.isLoading) {
      return {
        title: intl.formatMessage(config.title),
        data: [],
        isLoading: true,
      };
    }

    return transformPieChartData(
      chartData.data,
      intl.formatMessage(config.title),
      config.colors,
      theme,
    );
  });
};

export const formatMetricValue = (
  value: number | string,
  metricConfig: { type: MetricType },
): string => {
  if (typeof value === "string") {
    if (value === "Infinity") {
      return "Infinity";
    }
    const parsed = parseFloat(value);
    if (isNaN(parsed)) {
      return "0";
    }
    value = parsed;
  }

  const numValue = value as number;

  switch (metricConfig.type) {
    case "rate":
      return `${(numValue * 100).toFixed(1)}%`;
    case "percentage":
      return `${numValue.toFixed(1)}%`;
    case "number":
      return Math.round(numValue).toLocaleString();
    case "decimal":
      return numValue.toFixed(1);
    case "seconds":
      return `${numValue.toFixed(1)}s`;
    case "currency":
      return `$${Math.round(numValue).toLocaleString()}`;
    default:
      return Math.round(numValue).toLocaleString();
  }
};

const formatDateToISO = (date: Date): string => {
  return date.toISOString().split("T")[0];
};

export const getDaysFromPeriodType = (periodType: string): number => {
  switch (periodType) {
    case "last_7_days":
      return 7;
    case "last_14_days":
      return 14;
    case "last_30_days":
      return 30;
    case "last_60_days":
      return 60;
    case "last_90_days":
      return 90;
    case "last_180_days":
      return 180;
    default:
      return 30;
  }
};

const calculatePeriod = (endDate: Date, days: number) => {
  const startDate = new Date(endDate);
  startDate.setDate(endDate.getDate() - (days - 1));
  return {
    startDate: formatDateToISO(startDate),
    endDate: formatDateToISO(endDate),
  };
};

export const calculateDateRanges = (performanceDateRange: string) => {
  const days = getDaysFromPeriodType(performanceDateRange);
  const currentDate = new Date();
  // Exclude current date by going back one day
  const endDate = new Date(currentDate);
  endDate.setDate(currentDate.getDate() - 1);
  const currentPeriod = calculatePeriod(endDate, days);
  const comparisonEndDate = new Date(currentPeriod.startDate);
  comparisonEndDate.setDate(comparisonEndDate.getDate() - 1);
  const comparisonPeriod = calculatePeriod(comparisonEndDate, days);
  return { currentPeriod, comparisonPeriod };
};

export const createInitialMetricsState =
  (): PerformanceReportState["metrics"] => {
    const metrics = {} as PerformanceReportState["metrics"];

    Object.values(MetricCategory).forEach((category) => {
      metrics[category] = {
        category,
        metrics: {},
        isLoading: false,
      };
    });

    return metrics;
  };

export const processReportData = (
  currentData: any,
  comparisonData: any,
  requestedMetrics: { id: string; name: string }[],
) => {
  const processedMetrics: Record<string, any> = {};

  if (
    currentData?.metricHeaders &&
    currentData?.rows?.[0]?.metricValues &&
    comparisonData?.metricHeaders &&
    comparisonData?.rows?.[0]?.metricValues
  ) {
    const currentMetricMap = new Map();
    const comparisonMetricMap = new Map();

    currentData.metricHeaders.forEach(
      (header: { id: string }, index: number) => {
        currentMetricMap.set(header.id, index);
      },
    );

    comparisonData.metricHeaders.forEach(
      (header: { id: string }, index: number) => {
        comparisonMetricMap.set(header.id, index);
      },
    );

    requestedMetrics.forEach((metric) => {
      const currentIndex = currentMetricMap.get(metric.id);
      const comparisonIndex = comparisonMetricMap.get(metric.id);

      const currentValue =
        currentIndex !== undefined
          ? currentData.rows[0].metricValues[currentIndex] || 0
          : 0;
      const previousValue =
        comparisonIndex !== undefined
          ? comparisonData.rows[0].metricValues[comparisonIndex] || 0
          : 0;

      const percentageChange =
        previousValue === 0
          ? 0
          : ((currentValue - previousValue) / previousValue) * 100;

      const polarity = POSITIVE_METRICS.includes(metric.id)
        ? "positive"
        : "negative";

      processedMetrics[metric.id] = {
        current: currentValue,
        previous: previousValue,
        percentageChange,
        polarity,
      };
    });
  }

  return processedMetrics;
};

export const getKPIMetricConfig = (selectedView: string) => {
  const metricOption =
    KPI_PRODUCT_METRICS_OPTIONS.find(
      (option) => option.label === selectedView,
    ) ?? KPI_PRODUCT_METRICS_OPTIONS[0];

  const isMultiMetric = Array.isArray(metricOption.value);
  const metricsArray: string[] = isMultiMetric
    ? (metricOption.value as string[])
    : [metricOption.value as string];

  // Check if the metric has a sortBy array (for Hidden Gems and Traffic Jams)
  const hasSortByArray = (metricOption as any).sortBy;
  const sortMetric =
    (metricOption as any).sortMetric ||
    (isMultiMetric ? (metricOption.value as string[])[0] : metricOption.value);

  return {
    metric: metricsArray[0],
    metrics: metricsArray,
    sortMetric: sortMetric as string,
    sortDescending: (metricOption as any).sortDescending,
    sortBy: hasSortByArray ? (metricOption as any).sortBy : undefined,
    type: metricOption.type as MetricType,
    isMultiMetric,
  };
};

export const formatLocationValue = (value: number): string => {
  if (typeof value !== "number" || isNaN(value)) {
    return "0";
  }
  const absValue = Math.abs(value);
  if (absValue >= 1000000) {
    const millions = value / 1000000;
    return `${millions.toFixed(1).replace(".0", "")}M`;
  } else if (absValue >= 1000) {
    const thousands = value / 1000;
    return `${thousands.toFixed(1).replace(".0", "")}K`;
  } else {
    return value.toString();
  }
};

export const findNodeById = (nodes: any[], id: string): any | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children && Array.isArray(node.children)) {
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

export const findExpandedNodes = (
  nodes: any[],
  userCollapsedNodes: Set<string>,
): string[] => {
  const expanded: string[] = [];
  nodes.forEach((node) => {
    if (node.isExpanded && !userCollapsedNodes.has(node.id)) {
      expanded.push(node.id);
    }
    if (node.children && Array.isArray(node.children)) {
      expanded.push(...findExpandedNodes(node.children, userCollapsedNodes));
    }
  });
  return expanded;
};

export const buildLocationParentPath = (
  node: any,
): Array<{
  dimensionId: "country" | "region" | "city";
  dimensionItemId: string;
}> => {
  const parentPath = node.parentPath || [];
  return [
    ...parentPath,
    {
      dimensionId: node.dimension as "country" | "region" | "city",
      dimensionItemId: node.dimensionItemId,
    },
  ];
};

export const getNextLocationDimension = (
  currentDimension: string,
): "country" | "region" | "city" | null => {
  switch (currentDimension) {
    case "country":
      return "region";
    case "region":
      return "city";
    default:
      return null;
  }
};

export const updateCollapsedNodes = (
  currentCollapsedNodes: Set<string>,
  newlyCollapsed: string[],
): Set<string> => {
  const newSet = new Set(currentCollapsedNodes);
  newlyCollapsed.forEach((id) => newSet.add(id));
  return newSet;
};

export const updateExpandedNodes = (
  currentCollapsedNodes: Set<string>,
  newlyExpanded: string[],
): Set<string> => {
  const newSet = new Set(currentCollapsedNodes);
  newlyExpanded.forEach((id) => newSet.delete(id));
  return newSet;
};

export const findNodesToLoad = (
  treeData: any[],
  newlyExpanded: string[],
): Array<{ nodeId: string; node: any }> => {
  const nodesToLoad: Array<{ nodeId: string; node: any }> = [];
  newlyExpanded.forEach((nodeId) => {
    const node = findNodeById(treeData, nodeId);
    if (
      node &&
      node.hasChildren &&
      (!node.children || node.children.length === 0)
    ) {
      nodesToLoad.push({ nodeId, node });
    }
  });

  return nodesToLoad;
};

export const calculateNodeChanges = (
  expandedNodes: string[],
  nodeIds: string[],
): { newlyCollapsed: string[]; newlyExpanded: string[] } => {
  const newlyCollapsed = expandedNodes.filter((id) => !nodeIds.includes(id));
  const newlyExpanded = nodeIds.filter((id) => !expandedNodes.includes(id));
  return { newlyCollapsed, newlyExpanded };
};

export const transformPieChartData = (
  apiResponse: any,
  chartTitle: string,
  colors: string[],
  theme?: any,
): any => {
  if (!apiResponse?.rows || apiResponse.rows.length === 0) {
    return {
      title: chartTitle,
      data: [],
      isLoading: false,
    };
  }

  const resolveThemeColor = (colorPath: string): string => {
    if (!theme) return colorPath;

    const pathParts = colorPath.split(".");
    let color = theme.palette;

    for (const part of pathParts) {
      color = color?.[part];
    }

    return typeof color === "string" ? color : colorPath;
  };

  const data = apiResponse.rows.map((row: any, index: number) => ({
    name: row.dimensionValue || `Item ${index + 1}`,
    value: row.metricValues[0] || 0,
    color: resolveThemeColor(colors[index % colors.length]),
  }));

  data.sort((a, b) => b.value - a.value);

  return {
    title: chartTitle,
    data,
    isLoading: false,
  };
};

export const transformApiResponseToNodes = (
  apiResponse: any,
  dimension: string,
  parentPath: any[] = [],
) => {
  if (!apiResponse?.rows || !Array.isArray(apiResponse.rows)) {
    return [];
  }

  const transformedNodes = apiResponse.rows.map((row: any) => {
    const nodeId = `${dimension}-${row.dimensionItemId}`;
    const metricValue = row.metricValues?.[0] || 0;
    return {
      id: nodeId,
      dimensionValue: row.dimensionValue,
      dimensionItemId: row.dimensionItemId,
      metricValue,
      dimension: dimension as any,
      children: undefined,
      isLoading: false,
      isExpanded: false,
      hasChildren: dimension !== "city",
      parentPath,
    };
  });
  return transformedNodes;
};

export const updateNodeLoading = (
  nodes: LocationTreeNode[],
  nodeId: string,
  isLoading: boolean,
): LocationTreeNode[] => {
  return nodes.map((node) => {
    if (node.id === nodeId) {
      return { ...node, isLoading };
    }
    if (
      node.children &&
      Array.isArray(node.children) &&
      node.children.length > 0
    ) {
      return {
        ...node,
        children: updateNodeLoading(node.children, nodeId, isLoading),
      };
    }
    return node;
  });
};

export const addChildrenToNode = (
  nodes: LocationTreeNode[],
  parentNodeId: string,
  children: LocationTreeNode[],
): LocationTreeNode[] => {
  return nodes.map((node) => {
    if (node.id === parentNodeId) {
      return { ...node, children, isLoading: false, isExpanded: true };
    }
    if (
      node.children &&
      Array.isArray(node.children) &&
      node.children.length > 0
    ) {
      return {
        ...node,
        children: addChildrenToNode(node.children, parentNodeId, children),
      };
    }
    return node;
  });
};

export const transformDateYearMetricsData = (
  apiResponse: any,
  chartTitle: string,
  currentPeriodLabel: string = "Current Period",
  previousPeriodLabel: string = "Previous Period",
  granularity: "day" | "month" | "week" = "day",
  metricId?: string,
): any => {
  if (
    !apiResponse?.rows ||
    apiResponse.rows.length === 0 ||
    !apiResponse?.metricHeaders
  ) {
    return {
      title: chartTitle,
      data: [],
      isLoading: false,
      currentPeriodLabel,
      previousPeriodLabel,
    };
  }

  // Find the index of the specific metric in the metricHeaders
  const metricIndex = apiResponse.metricHeaders.findIndex(
    (header: any) => header.id === metricId,
  );

  if (metricIndex === -1) {
    return {
      title: chartTitle,
      data: [],
      isLoading: false,
      currentPeriodLabel,
      previousPeriodLabel,
    };
  }

  const dataMap = new Map();
  apiResponse.rows.forEach((row: any) => {
    const date = row.dimensionValue;
    const metricValue = row.metricValues[metricIndex] || 0;

    if (!dataMap.has(date)) {
      dataMap.set(date, { date, current: 0 });
    }

    dataMap.get(date).current = metricValue;
  });

  let data = Array.from(dataMap.values()).sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  // Format dates based on granularity
  data = data.map((item) => ({
    ...item,
    date: formatDateForGranularity(item.date, granularity),
  }));

  return {
    title: chartTitle,
    data,
    isLoading: false,
    currentPeriodLabel,
    previousPeriodLabel,
  };
};

const formatDateForGranularity = (
  dateString: string,
  granularity: "day" | "month" | "week",
): string => {
  let date: Date;

  if (granularity === "month") {
    // Handle yearMonth format (could be YYYY-MM, YYYYMM, or other formats)
    if (dateString.includes("-")) {
      // YYYY-MM format
      date = new Date(dateString + "-01"); // Add day to make it a valid date
    } else if (dateString.length === 6 && /^\d{6}$/.test(dateString)) {
      // YYYYMM format
      const year = parseInt(dateString.substring(0, 4));
      const month = parseInt(dateString.substring(4, 6)) - 1; // Month is 0-indexed
      date = new Date(year, month, 1);
    } else {
      // Fallback to regular date parsing
      date = new Date(dateString);
    }

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
    });
  } else if (granularity === "week") {
    // Handle YYYYMMDD format (e.g., "20251005") - calculate week number of the year
    if (dateString.length === 8 && /^\d{8}$/.test(dateString)) {
      const year = parseInt(dateString.substring(0, 4));
      const month = parseInt(dateString.substring(4, 6)) - 1; // Month is 0-indexed
      const day = parseInt(dateString.substring(6, 8));

      // Calculate ISO week number
      const d = new Date(Date.UTC(year, month, day));
      d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      const weekNumber = Math.ceil(
        ((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
      );

      return `Week ${weekNumber}`;
    }

    // Handle YYYYWW format (e.g., "202541") - last 2 digits are the actual week number
    if (dateString.length === 6 && /^\d{6}$/.test(dateString)) {
      const week = dateString.substring(4, 6);
      return `Week ${parseInt(week)}`;
    }

    // Handle YYYY-WW format (e.g., "2025-41")
    if (dateString.includes("-")) {
      const parts = dateString.split("-");
      if (parts.length === 2) {
        return `Week ${parseInt(parts[1])}`;
      }
    }
    return dateString; // Fallback
  } else {
    // Handle day format - YYYYMMDD format from API response
    if (dateString.length === 8 && /^\d{8}$/.test(dateString)) {
      // Parse YYYYMMDD format
      const year = parseInt(dateString.substring(0, 4));
      const month = parseInt(dateString.substring(4, 6)) - 1; // Month is 0-indexed
      const day = parseInt(dateString.substring(6, 8));
      date = new Date(year, month, day);
    } else {
      // Fallback to regular date parsing
      date = new Date(dateString);
    }

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }
};

export const getCurrentDate = () => {
  const currentDate = new Date();
  return currentDate.toISOString().split("T")[0];
};
